import React, { FC, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import {
  BuyStepper,
  ComponentSizes,
  PriceBlock
} from '@front-office/app-common-cart';
import {
  Checkbox,
  Image,
  Link,
  Title,
  Typography
} from '@npm-registry/eapteka-ui';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import {
  decreaseQuantity,
  increaseQuantity,
  markForDeletion,
  setQuantity
} from '../../../store/products';
import { text } from '../../../text';
// import { More } from '../More';
import { RecipeLabel } from '../RecipeLabel';
import { Sale } from '../Sale';
import { RecommendationProductList } from '../../blocks/RecommendationProductList';
import { RecommendationProductsStore } from '../../../store/recommendationProducts/types';
import { selectRecommendationProducts } from '../../../store/recommendationProducts';
import { ProductCommonCartProps } from '../../../store/products/types';
import { updateLoadingStatus } from '../../../store/summary';
import { Delivery } from '../Delivery';
import { Favorite } from '../Favorite';
import { Deletion } from '../Deletion';
import { Expiration } from '../Expiration';
import { combineVendorAndCountry } from './helpers/combineVendorAndCountry';
import { PreorderLabel } from '../Preorder';
import { ProductProps } from './types';
import styles from './Product.scss';

export const Product: FC<ProductProps> = ({
  id,
  sku,
  name,
  vendor,
  country,
  ingredient,
  isExpired,
  isDelayed,
  preorderDate,
  delivery,
  picture,
  availableCount,
  eRecipeNumber,
  promotion,
  unitQuantity,
  regionUrl
}) => {
  const dispatch: AppDispatch = useDispatch();
  const delayProduct = () => {
    // TODO:
  };
  const isFavorite: boolean = null;
  const isRecipeAvailable =
    ingredient !== null && eRecipeNumber !== null;
  const recommendationProducts = useSelector(
    (state: RecommendationProductsStore) =>
      selectRecommendationProducts(state, id)
  );

  const onDelete = useCallback(() => {
    dispatch(updateLoadingStatus('pending'));
    dispatch(markForDeletion([id]));
  }, [id, dispatch]);
  const productUrl = useMemo(
    () => `${regionUrl}goods/id${sku}`,
    [regionUrl, sku]
  );
  const stepperEventsHandlers = useMemo(
    () => ({
      setCartItemQuantity: ({
        newQuantity
      }: ProductCommonCartProps) =>
        dispatch(setQuantity({ id, newQuantity })),
      increaseCartItemQuantity: ({
        newQuantity
      }: ProductCommonCartProps) =>
        dispatch(increaseQuantity({ id, newQuantity })),
      decreaseCartItemQuantity: ({
        newQuantity
      }: ProductCommonCartProps) =>
        dispatch(decreaseQuantity({ id, newQuantity }))
    }),
    [id, dispatch]
  );

  const stepperEventsCustomHandlers = useMemo(
    () => ({
      removeItemFromCart: () =>
        dispatch(markForDeletion([id]))
    }),
    [id, dispatch]
  );

  const vendorAndCountry = combineVendorAndCountry(
    vendor,
    country
  );
  const rootClassNames = clsx([
    styles.root,
    {
      [styles.rootIsUnavailable]: availableCount <= 0,
      [styles.rootHasRecommendationProducts]:
        recommendationProducts?.length > 0
    }
  ]);

  return (
    <div className={rootClassNames}>
      <div className={styles.left}>
        <figure className={styles.picture}>
          <Link
            href={productUrl}
            className={styles.pictureLink}
          >
            <Image className={styles.image} src={picture} />
          </Link>
          {isFavorite !== null && (
            <Favorite
              className={styles.favorite}
              isActive={isFavorite}
              onClick={() => {
                //
              }}
            />
          )}
        </figure>
        {isDelayed !== null && (
          <Checkbox
            checked={!isDelayed}
            onChange={delayProduct}
          />
        )}
      </div>
      <div className={styles.details}>
        <Title tag="h6" className={styles.name}>
          <Link
            href={productUrl}
            className={styles.nameLink}
          >
            {name}
          </Link>
        </Title>
        <div className={styles.more}>
          {vendorAndCountry !== null && (
            <Link
              className={styles.vendor}
              href={vendorAndCountry.url}
            >
              {vendorAndCountry.name}
            </Link>
          )}
          {isRecipeAvailable && (
            <Link
              className={styles.ingredient}
              href={ingredient.url}
            >
              {text['ingredient.title']}: {ingredient.name}
            </Link>
          )}
        </div>
        <div className={styles.labels}>
          {preorderDate && (
            <PreorderLabel
              className={styles.preorder}
              date={preorderDate}
            />
          )}
          {delivery && delivery.text && (
            <Delivery
              className={styles.delivery}
              {...delivery}
            />
          )}
          {isRecipeAvailable && (
            <RecipeLabel title={ingredient.name} />
          )}
          {isExpired && (
            <Expiration text={text['expired.long']} />
          )}
          {promotion.length > 0 &&
            promotion.map((p, key) => (
              <Sale
                key={key}
                icon={p.icon}
                title={p.title}
              />
            ))}
        </div>
      </div>
      <div className={styles.controlsWrapper}>
        <div className={styles.priceBlock}>
          <PriceBlock
            id={id}
            options={{
              size: ComponentSizes.M,
              isWithLabel: false
            }}
          />
        </div>
        <div className={styles.controls}>
          {availableCount <= 0 ? (
            <Typography
              className={styles.unavailable}
              variant="p2"
            >
              {text['product.unavailable']}
            </Typography>
          ) : (
            <div className={styles.counter}>
              <BuyStepper
                id={id}
                isWithItemPrice
                size={ComponentSizes.S}
                unitQuantity={unitQuantity}
                availableCount={availableCount}
                eventsHandlers={stepperEventsHandlers}
                customHandlers={stepperEventsCustomHandlers}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.actions}>
        <Deletion callback={onDelete} />
      </div>
      <RecommendationProductList
        className={styles.recommendations}
        productId={id}
      />
    </div>
  );
};
